import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import Timer from "../../Timer";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStateStandard,
  editStateStandard,
  filterText,
  rowForEditStandard,
  selectedRowsStateStandard,
} from "../atoms/atoms";
import StandardForm from "../form/StandardForm";
import useDeleteStandard from "../hooks/useDeleteStandard";
import useGetStandard from "../hooks/useGetStandard";
import { StandardInfo } from "../type/StandardInterface";

export default function StandardTable() {
  //Hooks Call
  const {
    data: standardDataDirect,
    isLoading: isFetching,
    isSuccess: standardSuccess,
    refetch: refetch,
  } = useGetStandard();

  const BulkDeleteStandard = useDeleteStandard();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateStandard);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateStandard
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateStandard);
  const [filterTextState, setFilterTextState] = useRecoilState(filterText);
  const setEditRowData = useSetRecoilState(rowForEditStandard);
  const userPermissions = useRecoilValue(permissionsSelector);

  //Local States
  const [standardData, setStandardData] = useState<StandardInfo[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [standardFilteredData, setStandardFilteredData] = useState<
    StandardInfo[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_standard_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_standard_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_standard_delete;

  //For filtered data update
  const [standardId, setStandardId] = useState("");

  useEffect(() => {
    if (standardId !== "") {
      if (standardId?.includes(".")) {
        let ids = standardId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = standardData?.filter((x) =>
          arr.includes(x.standardId)
        );
        setStandardFilteredData(filteredData);
      } else {
        const filteredData = standardData?.filter(
          (x) => x.standardId === Number(standardId)
        );
        setStandardFilteredData(filteredData);
      }
    }
  }, [standardId, standardData]);

  useEffect(() => {
    if (standardSuccess && standardDataDirect) {
      setStandardData(standardDataDirect.map((data) => ({ ...data })));
    }
  }, [standardDataDirect, standardSuccess]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setStandardFilteredData(result);
          setStandardId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    const ids = selectedRows
      ? selectedRows.map((row) => row.standardId)
      : undefined;
    BulkDeleteStandard(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    refetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Standards" : "Standard"
    }?`}</div>
  );

  const filteredItems = standardData
    ? standardData?.filter(
        (item) =>
          item.standardName &&
          item.standardName
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const matColumns = [
    {
      title: "Standard Name",
      field: "standardName",
      customSort: (a, b) =>
        a.standardName && b.standardName
          ? a.standardName.trim().localeCompare(b.standardName.trim())
          : a.standardName
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdOn",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <StandardForm refetch={refetch} />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"Standard"}
                textInputName={"StandardName"}
                columnDetails={"Standard Name"}
                filterText={setFilterTextState}
                disabledButton={
                  selectedRows !== undefined && selectedRows.length > 0
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Standard"
                      onClick={() => handleAdd()}
                      disabled={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
            <div>
              <MaterialTableTS
                data={
                  standardFilteredData?.length > 0
                    ? standardFilteredData
                    : filterTextState === ""
                    ? standardData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Standard"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
